// src/components/NotificationCenter/index.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Badge,
  Popover,
  List,
  Tag,
  Button,
  Divider,
  Empty,
  Spin,
  notification as antdNotification,
  Avatar,
  message
} from 'antd';
import {
  BellOutlined,
  ReloadOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import wsSingleton from '@/utils/wsSingleton';
import { notificationService } from '../service';
import {
  NotificationType,
  UserNotificationView,
  SystemType
} from '../data';
import './index.less';

// 在组件顶部导入
import { useModel, useAccess } from '@umijs/max';

interface UserNotification extends UserNotificationView {}

// 图标映射
const getNotificationIcon = (type: NotificationType) => {
  const icons = {
    [NotificationType.Inform]: <InfoCircleOutlined style={{ color: '#1890ff' }} />,
    [NotificationType.Announcement]: <InfoCircleOutlined style={{ color: '#52c41a' }} />,
    [NotificationType.Expiration]: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
  };
  return icons[type] || <InfoCircleOutlined />;
};

// 类型颜色映射
const getTypeConfig = (type: NotificationType) => {
  const config = {
    [NotificationType.Inform]: { color: 'blue', text: '普通通知' },
    [NotificationType.Announcement]: { color: 'green', text: '公告' },
    [NotificationType.Expiration]: { color: 'red', text: '到期提醒' }
  };
  return config[type] || config[NotificationType.Inform];
};

// 系统类型文本映射
const getSystemTypeText = (systemType: SystemType): string => {
  switch (systemType) {
    case SystemType.All:
      return '全站';
    case SystemType.Website:
      return '网站系统';
    case SystemType.Software:
      return '软件系统';
    default:
      return '未知';
  }
};

// Ant Design 通知类型映射
const getNotificationAntdType = (type: NotificationType): 'info' | 'success' | 'error' | 'warning' => {
  const typeMap: Record<NotificationType, 'info' | 'success' | 'error' | 'warning'> = {
    [NotificationType.Inform]: 'info',
    [NotificationType.Announcement]: 'success',
    [NotificationType.Expiration]: 'error'
  };
  return typeMap[type] || 'info';
};

// 判断是否为需要显示的系统类型
const isDisplayableSystemType = (systemType: SystemType): boolean => {
  return systemType === SystemType.All || systemType === SystemType.Website;
};

const NotificationCenter: React.FC = () => {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const initialized = useRef(false);

  const { initialState } = useModel('@@initialState');
  const access = useAccess();
  const canManageNotification = access.canManageNotification;
  const currentUser = initialState?.currentUser;
  const userId = currentUser?.id;

  useEffect(() => {
    // 添加自定义动画样式
    const style = document.createElement('style');
    style.textContent = `
      .unread-dot {
        display: inline-block;
        width: 6px;
        height: 6px;
        background-color: #1890ff;
        border-radius: 50%;
        margin-left: 4px;
        animation: pulse 1.5s infinite;
      }
      
      .notification-item.unread {
        background-color: #f0f7ff;
        border-left: 2px solid #1890ff;
      }
      
      .notification-item.unread:hover {
        background-color: #e6f7ff;
      }
      
      .notification-item.read {
        opacity: 0.9;
      }
      
      .notification-item.read .ant-list-item-meta-description {
        color: #999;
      }
      
      .unread-badge {
        padding: 0 6px;
        height: 18px;
        line-height: 18px;
        font-size: 11px;
        background: #ff4d4f;
        color: white;
        border-radius: 9px;
        font-weight: 500;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (!initialState || !initialState.currentUser) {
      return;
    }
    
    const { currentUser } = initialState;
    const userId = currentUser.id;
    
    if (typeof userId !== 'number' || userId <= 0) {
      return;
    }

    if (!initialized.current) {
      initializeWebSocket();
      fetchNotifications();
      initialized.current = true;
    }

    return () => {
      // 清理
    };
  }, [initialState]);

  // 过滤通知函数
  const filterNotifications = (notificationsList: UserNotification[]): UserNotification[] => {
    return notificationsList.filter(notification => 
      isDisplayableSystemType(notification.systemType)
    );
  };

  // 计算过滤后的未读数量
  const calculateFilteredUnreadCount = (filteredList: UserNotification[]): number => {
    return filteredList.filter(notif => !notif.isRead).length;
  };

  useEffect(() => {
    const filtered = filterNotifications(notifications);
    setFilteredNotifications(filtered);
    setUnreadCount(calculateFilteredUnreadCount(filtered));
  }, [notifications]);

  const initializeWebSocket = () => {
    const wsUrl = process.env.REACT_APP_WS_URL || 'wss://admin.cybermindpro.com/api/ws';
    wsSingleton.init(wsUrl);
    
    wsSingleton.on('__raw', (rawMsg: any) => {
      try {
        if (typeof rawMsg === 'string') {
          const msg = JSON.parse(rawMsg);
          if (msg.type === 'notification' && msg.data) {
            handleNewNotification(msg.data);
          }
        }
      } catch (e) {
        console.log('消息不是 JSON:', rawMsg);
      }
    });
    
    wsSingleton.on('notification', (msg: any) => {
      if (msg.data) {
        handleNewNotification(msg.data);
      }
    });
    
    wsSingleton.on('new_notification', handleNewNotification);
    wsSingleton.on('__internal_open', handleReconnect);
    
    subscribeToNotifications();
  };

  const handleReconnect = () => {
    subscribeToNotifications();
    fetchNotifications();
  };

  const subscribeToNotifications = () => {
    if (!userId) {
      return false;
    }

    if (userId > 0) {
      if (wsSingleton.readyState !== WebSocket.OPEN) {
        return false;
      }

      const success = wsSingleton.send({
        type: 'subscribe',
        action: 'notifications',
        data: {
          userId: userId.toString(),
          timestamp: new Date().toISOString()
        }
      });

      return success;
    }

    return false;
  };

  const handleNewNotification = (newNotification: any) => {
    if (!newNotification) {
      return;
    }

    const notificationUserId = newNotification.userId || newNotification.UserId;
    if (notificationUserId && notificationUserId !== userId) {
      return;
    }

    const userNotification: UserNotification = {
      id: newNotification.id || Date.now(),
      notificationId: newNotification.notificationId || newNotification.id,
      title: newNotification.title || '新通知',
      content: newNotification.content || '',
      type: newNotification.type || NotificationType.Inform,
      systemType: newNotification.systemType || SystemType.All,
      isRead: false,
      readAt: undefined,
      createdAt: newNotification.createdAt || new Date().toISOString(),
    };

    // 只处理和显示符合条件的通知
    if (isDisplayableSystemType(userNotification.systemType)) {
      setNotifications(prev => [userNotification, ...prev]);
      
      if (!popoverVisible) {
        showAntdNotification(userNotification);
      }
    }
  };

  const showAntdNotification = (notification: UserNotification) => {
    const method = getNotificationAntdType(notification.type);

    const notificationConfig = {
      message: notification.title,
      description: (
        <div
          onClick={() => {
            setPopoverVisible(true);
            markAsRead(notification.id);
          }}
          style={{ cursor: 'pointer' }}
        >
          {formatContent(notification.content, true)}
        </div>
      ),
      placement: 'topRight' as const,
      duration: 6,
    };

    switch (method) {
      case 'success':
        antdNotification.success(notificationConfig);
        break;
      case 'error':
        antdNotification.error(notificationConfig);
        break;
      case 'warning':
        antdNotification.warning(notificationConfig);
        break;
      case 'info':
      default:
        antdNotification.info(notificationConfig);
        break;
    }
  };

  const showDesktopNotification = (notification: UserNotification) => {
    if (!('Notification' in window)) return;

    if (Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: formatContent(notification.content, true),
        icon: '/favicon.ico',
        tag: notification.id.toString()
      });
    } else if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  const formatContent = (content: string, plainText: boolean = false) => {
    if (!content) return '';

    if (plainText) {
      const cleanContent = content.replace(/<[^>]*>/g, '');
      return cleanContent.length > 100 ? cleanContent.substring(0, 100) + '...' : cleanContent;
    }

    return content.length > 100 ? content.substring(0, 100) + '...' : content;
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      console.log('开始获取通知数据...');
      console.log('请求参数:', { 
        skip: 0, 
        take: 10, 
        unreadOnly: false,
        userId 
      });
      
      const response = await notificationService.getUserNotifications({
        skip: 0,
        take: 10,
        unreadOnly: false
      });
      
      if (response && response.list) {
        const userNotifications: UserNotification[] = response.list
          .filter((item: any) => item != null)
          .map((item: UserNotificationView) => ({
            ...item,
          }));
        
        setNotifications(userNotifications);
      } else {
        console.error('响应格式错误:', response);
      }

    } catch (error: any) {
      console.error('获取通知失败:', error);
      console.error('错误详情:', error.message);
      message.error('获取通知失败，请检查网络连接');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: number) => {
    try {
      if (!currentUser) {
        message.warning('请先登录');
        return;
      }

      setNotifications(prev =>
        prev.map(notif =>
          notif.id === id ? { ...notif, isRead: true, readAt: new Date().toISOString() } : notif
        )
      );

      await notificationService.markAsRead(id);

      if (wsSingleton && wsSingleton.markNotificationAsRead) {
        wsSingleton.markNotificationAsRead(id.toString(), currentUser.id.toString());
      }

    } catch (error: any) {
      console.error('标记已读失败:', error);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === id ? { ...notif, isRead: false, readAt: undefined } : notif
        )
      );
      message.error('标记已读失败');
    }
  };

  const markAllAsRead = async () => {
    const unreadNotifications = filteredNotifications.filter(notif => !notif.isRead);
    if (unreadNotifications.length === 0) {
      message.info('没有未读通知');
      return;
    }

    try {
      const ids = unreadNotifications.map(notif => notif.id);

      setNotifications(prev =>
        prev.map(notif => 
          ids.includes(notif.id) 
            ? { ...notif, isRead: true, readAt: new Date().toISOString() } 
            : notif
        )
      );

      await notificationService.batchMarkAsRead({ ids });

      message.success(`已标记 ${unreadNotifications.length} 条通知为已读`);

    } catch (error: any) {
      console.error('标记所有已读失败:', error);
      fetchNotifications();
      message.error('标记所有已读失败');
    }
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '未知时间';

    try {
      const now = new Date();
      const time = new Date(timeStr);

      if (isNaN(time.getTime())) {
        return '无效时间';
      }

      const diff = now.getTime() - time.getTime();

      if (diff < 60000) return '刚刚';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`;

      return time.toLocaleDateString();
    } catch (error) {
      return '无效时间';
    }
  };

  const renderNotificationItem = (item: UserNotification) => {
    if (!item || typeof item !== 'object') {
      return null;
    }

    try {
      const typeConfig = getTypeConfig(item.type);

      return (
        <List.Item
          className={`notification-item ${item.isRead ? 'read' : 'unread'}`}
          onClick={() => !item.isRead && markAsRead(item.id)}
          style={{ 
            cursor: item.isRead ? 'default' : 'pointer',
            padding: '12px 16px',
            borderBottom: '1px solid #f5f5f5',
            transition: 'all 0.3s',
            position: 'relative'
          }}
        >
          <div className="notification-content" style={{ width: '100%' }}>
            {/* 未读状态指示条 */}
            {!item.isRead && (
              <div 
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  backgroundColor: '#1890ff',
                  borderTopLeftRadius: '2px',
                  borderBottomLeftRadius: '2px'
                }}
              />
            )}
            
            <div className="notification-item-header" style={{ 
              display: 'flex', 
              alignItems: 'flex-start',
              marginBottom: 8
            }}>
              <Avatar
                size="small"
                icon={getNotificationIcon(item.type)}
                style={{ 
                  marginRight: 8,
                  marginTop: 2,
                  color: !item.isRead ? '#1890ff' : '#bfbfbf',
                  backgroundColor: !item.isRead ? 'rgba(24, 144, 255, 0.1)' : '#f5f5f5'
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: 4 
                }}>
                  <span style={{ 
                    fontWeight: !item.isRead ? 600 : 400,
                    fontSize: '14px',
                    color: !item.isRead ? '#262626' : '#8c8c8c',
                    lineHeight: 1.4
                  }}>
                    {item.title || '无标题'}
                  </span>
                  {!item.isRead && <span className="unread-dot" />}
                </div>
                
                <Tag 
                  color={typeConfig.color} 
                  style={{ 
                    margin: 0,
                    fontSize: '11px',
                    lineHeight: '18px',
                    height: '18px',
                    padding: '0 6px'
                  }}
                >
                  {typeConfig.text}
                </Tag>
              </div>
            </div>
            
            <div
              className="notification-item-body"
              style={{
                fontSize: '13px',
                lineHeight: 1.5,
                marginBottom: 8,
                color: !item.isRead ? '#595959' : '#999'
              }}
              dangerouslySetInnerHTML={{ __html: formatContent(item.content) }}
            />
            
            <div className="notification-item-meta" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '12px',
              color: '#999'
            }}>
              <div className="notification-item-time" style={{ display: 'flex', alignItems: 'center' }}>
                <ClockCircleOutlined style={{ marginRight: 4, fontSize: 12 }} />
                {formatTime(item.createdAt)}
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="notification-item-system">
                  {getSystemTypeText(item.systemType)}
                </div>
                
                {!item.isRead && (
                  <span className="unread-badge">
                    未读
                  </span>
                )}
              </div>
            </div>
          </div>
        </List.Item>
      );
    } catch (error) {
      return (
        <List.Item>
          <div style={{ color: '#ff4d4f', padding: '12px 16px' }}>
            通知内容渲染出错
          </div>
        </List.Item>
      );
    }
  };

  const notificationContent = (
    <div className="notification-popover">
      <div className="notification-header">
        <span className="notification-title">通知中心</span>
        <div className="notification-actions">
          <Button
            type="text"
            icon={<ReloadOutlined />}
            onClick={fetchNotifications}
            size="small"
            loading={loading}
            title="刷新"
          />
          <Button
            type="text"
            icon={<CheckOutlined />}
            onClick={markAllAsRead}
            size="small"
            disabled={unreadCount === 0}
            title="全部已读"
          >
            全部已读
          </Button>
        </div>
      </div>

      <Divider style={{ margin: 0 }} />

      <div 
        className="notification-list"
        style={{ 
          flex: 1,
          overflowY: 'auto',
          maxHeight: 400
        }}
      >
        {loading ? (
          <div className="notification-loading">
            <Spin size="small" />
            <div style={{ marginTop: 8 }}>加载中...</div>
          </div>
        ) : filteredNotifications.length > 0 ? (
          <List
            dataSource={filteredNotifications}
            renderItem={renderNotificationItem}
            locale={{ emptyText: '暂无通知' }}
            style={{ padding: 0 }}
            itemLayout="vertical"
            split={false}
          />
        ) : (
          <div style={{ padding: 40 }}>
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
              description="暂无通知"
              imageStyle={{ height: 60 }}
            />
          </div>
        )}
      </div>

      {filteredNotifications.length > 0 && (
        <>
          <Divider style={{ margin: 0 }} />
          <div className="notification-footer" style={{ 
            padding: '8px 16px', 
            textAlign: 'center',
            fontSize: '12px',
            color: '#999',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <span>共 {filteredNotifications.length} 条通知</span>
              {unreadCount > 0 && (
                <span style={{ marginLeft: 12, color: '#ff4d4f', fontWeight: 500 }}>
                  未读: {unreadCount}
                </span>
              )}
            </div>
            {canManageNotification && (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  window.location.href = '/notifications';
                }}
                style={{ padding: 0, height: 'auto' }}
              >
                查看全部
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );

  return (
    <Popover
      content={notificationContent}
      trigger="click"
      placement="bottomRight"
      open={popoverVisible}
      onOpenChange={setPopoverVisible}
      overlayClassName="notification-popover-container"
      overlayStyle={{ padding: 0 }}
    >
      <div className="notification-badge-wrapper">
        <Badge
          count={unreadCount > 0 ? (unreadCount > 99 ? '99+' : unreadCount) : 0}
          size="small"
          offset={[-5, 5]}
          overflowCount={99}
          className="notification-badge"
        >
          <div className="notification-trigger">
            <BellOutlined />
            {unreadCount > 0 && (
              <div 
                className="notification-pulse"
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  width: 8,
                  height: 8,
                  backgroundColor: '#ff4d4f',
                  borderRadius: '50%',
                  animation: 'pulse 1.5s infinite'
                }}
              />
            )}
          </div>
        </Badge>
      </div>
    </Popover>
  );
};

export default NotificationCenter;